import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons'
import { cva } from 'class-variance-authority'
import { CommandList } from 'cmdk'
import { cn } from 'lib/utils'
import { ChevronDown, Loader2 } from 'lucide-react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from 'primitives/command'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'
import { Tooltip } from 'primitives/tooltip'
import React from 'react'
import { Label } from './label'
import { Typography } from './typography'

export interface SelectOption {
  value: string
  label: string | React.ReactNode
  icon?: React.ReactNode
  searchValue?: string
  disabled?: boolean
  tooltipContent?: string
  [key: string]: unknown
}

export interface SelectGroupProps {
  id: string
  label: string | React.ReactNode
  options: SelectOption[]
}

export interface SelectWithGroupsProps {
  name?: string
  label?: string
  groups: SelectGroupProps[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  inputPlaceholder?: string
  emptyPlaceholder?: string
  className?: string
  disabled?: boolean
  variant?: 'default' | 'ghost'
  popoverContentClassName?: string
  size?: 'sm' | 'md' | 'lg'
  helperText?: string
  error?: React.ReactNode
  loading?: boolean
  inline?: boolean
}

interface OptionItemProps {
  option: SelectOption
  value: string | undefined
  handleSelect: (value: string) => void
}

const OptionItem: React.FC<OptionItemProps> = ({ option, value, handleSelect }) => {
  const content = (
    <CommandItem
      key={option.value}
      onSelect={() => !option.disabled && handleSelect(option.value)}
      disabled={option.disabled}
      data-testid={option.value}
      className={cn(option.disabled && 'opacity-50 cursor-not-allowed')}
    >
      <span
        className="truncate flex items-center gap-2"
        title={option.searchValue || (option.label as string)}
        data-test-value={option.value}
      >
        {option.icon} {option.label}
      </span>
      <CheckIcon className={cn('ml-auto', option.value === value ? 'opacity-100' : 'opacity-0')} />
    </CommandItem>
  )

  if (option.disabled && option.tooltipContent) {
    return (
      <Tooltip content={option.tooltipContent} side="right">
        {content}
      </Tooltip>
    )
  }

  return content
}

const selectStyles = cva('font-inter text-p-400 normal-case tracking-normal', {
  variants: {
    variant: {
      ghost: 'w-28',
      default: 'w-full',
    },
    size: {
      sm: 'min-h-[26px]',
      md: 'min-h-[36px]',
      lg: 'min-h-[42px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export const SelectWithGroups = React.forwardRef<HTMLInputElement, SelectWithGroupsProps>(
  (
    {
      inputPlaceholder,
      emptyPlaceholder,
      placeholder,
      className,
      groups = [],
      value,
      label,
      onChange,
      disabled,
      variant = 'default',
      size = 'md',
      name,
      popoverContentClassName = 'w-[var(--radix-popover-trigger-width)]',
      helperText = '',
      error,
      loading = false,
      inline = false,
    },
    ref,
  ) => {
    const [searchTerm, setSearchTerm] = React.useState<string>('')
    const [isOpen, setIsOpen] = React.useState(false)
    const triggerButtonRef = React.useRef<HTMLButtonElement>(null)

    // Filter groups and their options based on search term
    const filteredGroups = groups
      ?.map((group) => ({
        ...group,
        options: group?.options?.filter(
          (option) =>
            (typeof option.label === 'string' &&
              option.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
            option.searchValue?.toLowerCase()?.includes(searchTerm.toLowerCase()),
        ),
      }))
      .filter((group) => group?.options?.length > 0)

    // Handle selection
    const handleSelect = (selectedValue: string) => {
      if (!selectedValue) return
      onChange?.(selectedValue)
      setIsOpen(false)
      setSearchTerm('')
    }

    // Find selected option across all groups
    const selectedOption = groups
      .flatMap((group) => group?.options)
      .find((option) => option?.value === value)

    // Render content based on conditions
    const content = value ? (
      <span
        className={cn('truncate flex items-center gap-2', !selectedOption && 'text-status-danger')}
      >
        {selectedOption?.icon} {selectedOption?.label || value}
      </span>
    ) : (
      <span className="mr-auto text-muted-foreground">{placeholder}</span>
    )

    return (
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        modal
        data-state={loading ? 'closed' : isOpen ? 'open' : 'closed'}
      >
        <PopoverTrigger
          asChild
          className={cn(disabled && 'pointer-events-none', loading && 'pointer-events-none')}
        >
          <div className={cn(selectStyles({ variant, size }), className)}>
            {label && (
              <Label className="mb-1.5 block" htmlFor={name}>
                {label}
              </Label>
            )}
            <button
              ref={triggerButtonRef}
              type="button"
              id={name}
              className={cn(
                'flex w-full focus-visible cursor-pointer items-center px-3 py-1 data-[state=open]:border-ring',
                selectStyles({ size }),
                {
                  'justify-end border-none focus:ring-0 w-20': variant === 'ghost',
                  'justify-between border rounded-md bg-background': variant === 'default',
                },
                disabled || loading ? 'bg-muted text-muted-foreground pointer-events-none' : '',
                (disabled || loading) && variant === 'ghost' ? 'bg-transparent text-tertiary' : '',
                isOpen && variant !== 'ghost' && 'ring-ring ring-2',
                error && 'border-status-danger',
                className,
              )}
            >
              <div className="truncate">{content}</div>

              {!inline && (
                <div className="flex items-center pl-1 text-muted-foreground/60 hover:text-foreground">
                  {React.createElement(loading ? Loader2 : ChevronDown, {
                    className: `size-4${loading ? ' animate-spin' : ''}`,
                  })}
                </div>
              )}
            </button>

            {helperText && (
              <Typography variant="XS/400" className="text-tertiary mt-2">
                {helperText}
              </Typography>
            )}
            {error && (
              <Typography variant="XS/400" className="text-status-danger mt-2">
                {error}
              </Typography>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            triggerButtonRef.current?.focus()
          }}
          className={cn('p-0', popoverContentClassName)}
          align="start"
        >
          <Command shouldFilter={false}>
            <div className="relative">
              <CommandInput
                value={searchTerm}
                onValueChange={(e) => setSearchTerm(e)}
                ref={ref}
                placeholder={inputPlaceholder ?? 'Search...'}
                className="h-9"
              />
              {searchTerm && (
                <button
                  aria-label="Clear search"
                  type="button"
                  className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchTerm('')}
                >
                  <Cross2Icon className="size-4" />
                </button>
              )}
            </div>
            <CommandEmpty>{emptyPlaceholder ?? 'No results found.'}</CommandEmpty>
            <CommandList className="max-h-[200px] overflow-auto">
              {filteredGroups.map((group) => (
                <CommandGroup key={group.id} heading={group.label}>
                  {group.options.map((option) => (
                    <OptionItem
                      key={option.value}
                      option={option}
                      value={value}
                      handleSelect={handleSelect}
                    />
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)
